/**
 * The store's motion: a light that follows the pointer over a card and tilts it a little,
 * and cards that rise in as they scroll into view. All of it stands still for whoever
 * asks the system for less motion, and the tilt is for a mouse alone.
 */

import { useEffect, useRef, useState } from 'react'

const still = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
const mouse = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches

/** At most this many degrees each way. */
const TILT = 5

export function useSpotlight<T extends HTMLElement>({ tilt = false } = {}) {
  const ref = useRef<T | null>(null)
  useEffect(() => {
    const node = ref.current
    if (!node) return
    const tilting = tilt && mouse() && !still()
    const move = (event: PointerEvent) => {
      const box = node.getBoundingClientRect()
      const x = event.clientX - box.left
      const y = event.clientY - box.top
      node.style.setProperty('--x', `${x}px`)
      node.style.setProperty('--y', `${y}px`)
      if (tilting) {
        node.style.setProperty('--ry', `${((x / box.width) * 2 - 1) * TILT}deg`)
        node.style.setProperty('--rx', `${(1 - (y / box.height) * 2) * TILT}deg`)
      }
    }
    const leave = () => {
      node.style.setProperty('--rx', '0deg')
      node.style.setProperty('--ry', '0deg')
    }
    node.addEventListener('pointermove', move)
    node.addEventListener('pointerleave', leave)
    return () => {
      node.removeEventListener('pointermove', move)
      node.removeEventListener('pointerleave', leave)
    }
  }, [tilt])
  return ref
}

/** True once the element has scrolled into view; it stays true. */
export function useRevealed<T extends HTMLElement>() {
  const ref = useRef<T | null>(null)
  const [shown, setShown] = useState(false)
  useEffect(() => {
    const node = ref.current
    if (!node) return
    if (still() || !('IntersectionObserver' in window)) {
      setShown(true)
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true)
          observer.disconnect()
        }
      },
      { rootMargin: '0px 0px -6% 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  return [ref, shown] as const
}
